import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Stack,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Snackbar,
  FormControlLabel,
  Checkbox,
  Divider,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { 
  updatePassword, 
  EmailAuthProvider, 
  reauthenticateWithCredential,
  verifyBeforeUpdateEmail,
  sendEmailVerification
} from 'firebase/auth';
import { getCurrentUser, updatePreferences } from '../services/api';

const UserProfileSettings = () => {
  const { user } = useAuth();
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isReauthDialogOpen, setIsReauthDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [verificationEmailSent, setVerificationEmailSent] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [communicationPrefs, setCommunicationPrefs] = useState({
    product_updates: false
  });

  useEffect(() => {
    // Load user preferences when component mounts
    const loadUserPreferences = async () => {
      try {
        const userData = await getCurrentUser();
        if (userData?.meta?.communication_preferences) {
          setCommunicationPrefs(userData.meta.communication_preferences);
        }
      } catch (error) {
        console.error('Failed to load user preferences:', error);
      }
    };
    
    loadUserPreferences();
  }, []);

  const resetForm = () => {
    setNewEmail('');
    setNewPassword('');
    setConfirmPassword('');
    setCurrentPassword('');
    setError('');
    setSuccess('');
    setVerificationEmailSent(false);
  };

  const showNotification = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleReauthentication = async () => {
    try {
      const credential = EmailAuthProvider.credential(
        user.firebaseUser.email,
        currentPassword
      );
      await reauthenticateWithCredential(user.firebaseUser, credential);
      setIsReauthDialogOpen(false);
      
      if (pendingAction === 'email') {
        await handleEmailUpdate();
      } else if (pendingAction === 'password') {
        try {
          await updatePassword(user.firebaseUser, newPassword);
          showNotification('Password updated successfully');
          resetForm();
        } catch (error) {
          showNotification(error.message || 'Failed to update password', 'error');
        }
      }
      
      setCurrentPassword('');
      setPendingAction(null);
    } catch (error) {
      showNotification('Invalid password. Please try again.', 'error');
    }
  };

  const handleEmailUpdate = async () => {
    try {
      await verifyBeforeUpdateEmail(user.firebaseUser, newEmail);
      setVerificationEmailSent(true);
      showNotification('Verification email sent to your new address. Please check your inbox and click the verification link.');
    } catch (error) {
      if (error.code === 'auth/requires-recent-login') {
        setPendingAction('email');
        setIsReauthDialogOpen(true);
      } else if (error.code === 'auth/invalid-email') {
        showNotification('Please enter a valid email address.', 'error');
      } else if (error.code === 'auth/email-already-in-use') {
        showNotification('This email is already in use by another account.', 'error');
      } else {
        showNotification(error.message, 'error');
      }
    }
  };

  const handleResendVerification = async () => {
    try {
      await sendEmailVerification(user.firebaseUser);
      showNotification('Verification email sent! Please check your inbox.');
    } catch (error) {
      showNotification('Error sending verification email. Please try again later.', 'error');
    }
  };

  const handlePasswordUpdate = async () => {
    if (newPassword !== confirmPassword) {
      showNotification('Passwords do not match', 'error');
      return;
    }

    if (newPassword.length < 6) {
      showNotification('Password must be at least 6 characters long', 'error');
      return;
    }

    try {
      // Always require re-authentication for password changes
      setPendingAction('password');
      setIsReauthDialogOpen(true);
    } catch (error) {
      showNotification(error.message, 'error');
    }
  };

  const handleCommunicationPrefsChange = async (event) => {
    const newPrefs = {
      ...communicationPrefs,
      [event.target.name]: event.target.checked
    };
    
    try {
      await updatePreferences({
        communication_preferences: newPrefs
      });
      setCommunicationPrefs(newPrefs);
      showNotification('Communication preferences updated successfully');
    } catch (error) {
      showNotification('Failed to update communication preferences', 'error');
      // Revert the change in UI
      setCommunicationPrefs(communicationPrefs);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary', fontWeight: 'medium' }}>
        User Profile
      </Typography>
      
      <Stack spacing={3}>
        <Paper elevation={0} variant="outlined">
          <Stack spacing={3} sx={{ p: 3 }}>
            {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>}
            
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Current Email</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography color="text.secondary">{user?.firebaseUser?.email}</Typography>
                <Chip 
                  size="small"
                  color={user?.firebaseUser?.emailVerified ? "success" : "warning"}
                  label={user?.firebaseUser?.emailVerified ? "Verified" : "Not Verified"}
                />
                {!user?.firebaseUser?.emailVerified && (
                  <Button
                    size="small"
                    onClick={handleResendVerification}
                    variant="outlined"
                  >
                    Resend Verification
                  </Button>
                )}
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Change Email</Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="New Email"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  disabled={verificationEmailSent}
                />
                {verificationEmailSent ? (
                  <Alert severity="info">
                    Please check your new email address for a verification link. 
                    Once verified, your email will be updated automatically.
                  </Alert>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleEmailUpdate}
                    disabled={!newEmail}
                  >
                    Update Email
                  </Button>
                )}
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Change Password</Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="New Password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <Button
                  variant="contained"
                  onClick={handlePasswordUpdate}
                  disabled={!newPassword || !confirmPassword}
                >
                  Update Password
                </Button>
              </Stack>
            </Box>
          </Stack>
        </Paper>

        <Paper elevation={0} variant="outlined">
          <Box sx={{ p: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
              Communication Preferences
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose how you want to stay updated about our product and services.
            </Typography>

            <Box sx={{ 
              p: 2, 
              bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
              borderRadius: 1
            }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={communicationPrefs.product_updates}
                    onChange={handleCommunicationPrefsChange}
                    name="product_updates"
                    color="primary"
                    sx={{ 
                      '& .MuiSvgIcon-root': {
                        fontSize: 24,
                      }
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" color="text.primary" sx={{ mb: 0.5 }}>
                      Product Updates & Features
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Get notified about new features and improvements to help you create better content
                    </Typography>
                  </Box>
                }
                sx={{ 
                  m: 0,
                  alignItems: 'flex-start',
                  '& .MuiFormControlLabel-label': {
                    flex: 1,
                    mt: 0.5
                  }
                }}
              />
            </Box>
          </Box>
        </Paper>
      </Stack>

      <Dialog open={isReauthDialogOpen} onClose={() => setIsReauthDialogOpen(false)}>
        <DialogTitle>Verify Your Identity</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            For security reasons, please enter your current password to continue.
          </Typography>
          <TextField
            fullWidth
            label="Current Password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsReauthDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleReauthentication} variant="contained">
            Verify
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserProfileSettings; 